import { useState } from 'preact/hooks';
import { ApiError, api } from '../../services/api.js';
import { Button } from '../ui/Button.js';
import { Sheet } from '../ui/Sheet.js';

/**
 * 407 (#1370, mock-up screens 2–3): the customer ends their booking. A rental stays active past its return until it
 * is ended; the platform decides whether it can be (`can_end` on `GET /bookings/:id`, the rules in
 * `backend/src/modules/bookings/endRental.ts`), so this only asks once and calls `POST /bookings/:id/end`. A refusal
 * shows the platform's own sentence.
 */
export function EndBookingAction({
  bookingId,
  onEnded,
}: { bookingId: string; onEnded: () => void }) {
  const [asking, setAsking] = useState(false);
  const [ending, setEnding] = useState(false);
  const [error, setError] = useState('');

  async function end() {
    setEnding(true);
    setError('');
    try {
      await api.post(`/bookings/${bookingId}/end`);
      setAsking(false);
      onEnded();
    } catch (err) {
      setError(
        err instanceof ApiError && err.message
          ? err.message
          : 'Your booking could not be ended. Please try again.',
      );
    } finally {
      setEnding(false);
    }
  }

  return (
    <div data-testid="end-booking" className="mt-3 border-t border-surface-mid pt-3">
      <p data-testid="end-booking-note" className="mb-2 text-xs text-text-muted">
        Ending tells us you have returned the car.
      </p>
      <Button size="lg" className="w-full" onClick={() => setAsking(true)}>
        End booking
      </Button>
      {error && !asking && (
        <p role="alert" className="mt-2 text-center text-sm text-red-400">
          {error}
        </p>
      )}
      <Sheet
        open={asking}
        title="End this booking?"
        onDismiss={() => setAsking(false)}
        footer={
          <div className="flex w-full flex-col gap-2">
            <Button size="lg" className="w-full" loading={ending} onClick={() => void end()}>
              End booking
            </Button>
            <Button variant="ghost" size="lg" className="w-full" onClick={() => setAsking(false)}>
              Not yet
            </Button>
          </div>
        }
      >
        <p className="text-sm text-text-muted">
          You can't undo this. Your receipt will be ready right after.
        </p>
        {error && (
          <p role="alert" className="text-sm text-red-400">
            {error}
          </p>
        )}
      </Sheet>
    </div>
  );
}
